import axios from 'axios'
import OTPAuth from 'otpauth'
import SecureLS from 'secure-ls'
const AUTH_API_URL = process.env.REACT_APP_SSO_API_URL
const NAMESPACE = process.env.REACT_APP_SSO_NAMESPACE
const SECRET = process.env.REACT_APP_SSO_SECRET
const STORAGE_SECRET = process.env.REACT_APP_SSO_STORAGE_SECRET

function SSOClient(config) {
  let { namespace, secret, storageSecret, url } = config ? config : {}
  if (!namespace) namespace = NAMESPACE
  if (!secret) secret = SECRET
  if (!storageSecret) storageSecret = STORAGE_SECRET
  if (!url) url = AUTH_API_URL

  this.namespace = namespace
  this.secret = secret
  this.url = url
  this.token = null
  this.rToken = null
  this.user = null
  this.refreshPromise = null
  this.ls = new SecureLS({
    isCompression: true,
    encodingType: 'aes',
    encryptionSecret: btoa(`LS-${secret}-${storageSecret}`)
  })

  this.x = axios.create({
    baseURL: this.url,
    headers: {
      common: {
        'X-API-SECRET': this.secret,
        'X-API-NAMESPACE': this.namespace
      }
    }
  })
  this.store = {
    get: (key) => {
      return this.ls.get(`${key}[${this.namespace}]`)
    },
    set: (key, value) => {
      this.ls.set(`${key}[${this.namespace}]`, value)
    },
    remove: (key) => {
      this.ls.remove(`${key}[${this.namespace}]`)
    }
  }
  this.authStateSubscribers = []
  setTimeout(() => {
    this.store.remove('shppr:sso:u')
    this.getUser().then((user) => {
      console.info(`SSO client for namespace '${this.namespace}' initialized.`)
      this.dispatchStateChange('initialized')
    })
  }, 1000)
}

SSOClient.prototype.isExpired = function (tokenData) {
  return !tokenData || tokenData.e * 1000 < Date.now()
}

SSOClient.prototype.clearSessionCache = function ({ preserveRefreshToken = false } = {}) {
  this.token = null
  this.user = null

  if (!preserveRefreshToken) {
    this.rToken = null
  }
}

SSOClient.prototype.initializeWithRefreshToken = async function (refreshToken) {
  if (!refreshToken) throw new Error('No token')
  await this.login({ refreshToken })
  this.dispatchLogIn()
  console.info(`SSO client for namespace '${this.namespace}' initialized.`)
  this.dispatchStateChange('initialized')
}

SSOClient.prototype.dispatchStateChange = function (state) {
  console.info(`STATE CHANGE FOR ${this.namespace}: ${state}`)
  for (const fn of this.authStateSubscribers) {
    fn(state)
  }
}

SSOClient.prototype.getRefreshToken = function () {
  if (!this.rToken) {
    const rToken = this.store.get('shppr:sso:rtk')
    if (!rToken) return null
    this.rToken = JSON.parse(rToken)
  }
  if (this.isExpired(this.rToken)) {
    this.rToken = null
    this.store.remove('shppr:sso:rtk')
    return null
  }
  return this.rToken.t
}

SSOClient.prototype.getStoredUser = function () {
  const userHash = this.store.get('shppr:sso:u')
  if (!userHash) return null

  try {
    return JSON.parse(atob(userHash))
  } catch {
    this.store.remove('shppr:sso:u')
    return null
  }
}

SSOClient.prototype.persistAuthSession = function (tokenData, { preserveUser = false } = {}) {
  const tokenLife = 60 * 60 // 1 hora
  const refreshTokenLife = 30 * 24 * 60 * 60 // 30 dias
  const creation = tokenData.creation ?? Math.floor(Date.now() / 1000)

  this.token = {
    t: tokenData.token,
    e: creation + tokenLife
  }
  this.store.set('shppr:sso:atk', JSON.stringify(this.token))

  if (tokenData.refreshToken) {
    this.rToken = {
      t: tokenData.refreshToken,
      e: creation + refreshTokenLife
    }
    this.store.set('shppr:sso:rtk', JSON.stringify(this.rToken))
  }

  const nextUser = tokenData.userProfile ?? (preserveUser ? this.user || this.getStoredUser() : null)

  if (nextUser) {
    this.user = nextUser
    this.store.set('shppr:sso:u', btoa(JSON.stringify(nextUser)))
    return
  }

  if (!preserveUser) {
    this.user = null
    this.store.remove('shppr:sso:u')
  }
}

SSOClient.prototype.getToken = async function () {
  if (!this.token) {
    const tokenData = this.store.get('shppr:sso:atk')
    if (tokenData) this.token = JSON.parse(tokenData)
  }

  if (this.isExpired(this.token)) {
    const refreshed = await this.refreshToken()
    if (!refreshed || this.isExpired(this.token)) {
      return null
    }
  }
  return this.token?.t || null
}

SSOClient.prototype.getUser = async function (uid, allowRefreshRetry = true) {
  if (!uid) {
    const cachedUser = this.user || this.getStoredUser()

    if (cachedUser) {
      const token = await this.getToken()

      if (!token) {
        this.logout()
        return null
      }

      this.user = cachedUser
      return cachedUser
    }

    const token = await this.getToken()
    if (!token) {
      this.logout()
      return null
    }
    try {
      const response = await this.x.post('/auth/profile', { token })
      const user = response.data.data
      this.store.set('shppr:sso:u', btoa(JSON.stringify(user)))
      this.user = user
      return user
    } catch (e) {
      console.trace(e)
      if (e?.response?.status === 401 && allowRefreshRetry) {
        const renovation = await this.refreshToken()
        if (renovation) return this.getUser(undefined, false)
      }
      if (e?.response?.status === 401) {
        this.logout()
      }
      return null
    }
  } else {
    const userHash = this.store.get(`shppr:sso:p:${uid}`)
    if (userHash) {
      return JSON.parse(atob(userHash))
    } else {
      const token = await this.getToken()
      if (!token) {
        this.logout()
        return null
      }
      try {
        const response = await this.x.post('/auth/profile', { uid })
        const user = response.data.data
        this.store.set(`shppr:sso:p:${uid}`, btoa(JSON.stringify(user)))
        return user
      } catch (e) {
        console.trace(e)
        if (e?.response?.status === 401 && allowRefreshRetry) {
          const renovation = await this.refreshToken()
          if (renovation) return this.getUser(uid, false)
        }
        if (e?.response?.status === 401) {
          this.logout()
        }
        return null
      }
    }
  }
}

SSOClient.prototype.refreshToken = async function () {
  if (this.refreshPromise) {
    return this.refreshPromise
  }

  const rToken = this.getRefreshToken()
  if (!rToken) return null

  this.refreshPromise = (async () => {
    try {
      await this.login({ refreshToken: rToken }, { preserveUser: true })
      return true
    } catch (e) {
      console.trace(e)
      return false
    } finally {
      this.refreshPromise = null
    }
  })()

  return this.refreshPromise
}

SSOClient.prototype.login = async function (user, options = {}) {
  const response = await this.x.post('/auth', user)
  const tokenData = response.data.data
  this.persistAuthSession(tokenData, options)
  return tokenData
}

SSOClient.prototype.dispatchLogIn = async function () {
  this.dispatchStateChange('logged_in')
}

SSOClient.prototype.logout = function () {
  this.clearSessionCache()
  this.store.remove('shppr:sso:atk')
  this.store.remove('shppr:sso:rtk')
  this.store.remove('shppr:sso:u')
  this.dispatchStateChange('logged_out')
}

SSOClient.prototype.lock = function () {
  this.clearSessionCache({ preserveRefreshToken: true })
  this.store.remove('shppr:sso:atk')
  this.store.remove('shppr:sso:u')
  this.dispatchStateChange('locked')
}

SSOClient.prototype.unlock = async function () {
  const result = await this.refreshToken()
  if (result) this.dispatchStateChange('unlocked')
  return result
}

SSOClient.prototype.checkClaim = async function (claim, force) {
  if (!force) {
    let claimCache = this.store.get(`shppr:sso:cck:${claim}`)
    if (claimCache) {
      claimCache = JSON.parse(atob(claimCache))
      const now = Date.now()
      if (now < claimCache.exp) {
        return claimCache.authorization
      } else {
        this.store.remove(`shppr:sso:cck:${claim}`)
      }
    }
  }
  const token = await this.getToken()
  if (!token) {
    this.logout()
    return false
  }
  try {
    const response = await this.x.post('/auth/check', { token, claim })
    const authorization = response.data.data
    this.store.set(
      `shppr:sso:cck:${claim}`,
      btoa(
        JSON.stringify({
          authorization,
          exp: Date.now() + 60 * 60 * 1000 // 1 hora de cache
        })
      )
    )
    return authorization
  } catch (e) {
    console.trace(e)
    if (e.response.status === 401) {
      const renovation = await this.refreshToken()
      if (renovation) {
        const refreshedToken = await this.getToken()
        if (!refreshedToken) {
          this.logout()
          return false
        }
        const response = await this.x.post('/auth/check', { token: refreshedToken, claim })
        const authorization = response.data.data
        this.store.set(
          `shppr:sso:cck:${claim}`,
          btoa(
            JSON.stringify({
              authorization,
              exp: Date.now() + 60 * 60 * 1000 // 1 hora de cache
            })
          )
        )
        return authorization
      }
      this.logout()
    }
    return false
  }
}

SSOClient.prototype.delegateToken = async function (namespace) {
  const token = await this.getToken()
  const response = await this.x.post('/auth/delegate', {
    token,
    namespace
  })
  return response.data.data
}

SSOClient.prototype.generateTotp = async function () {
  const period = 30
  const digits = 6
  const user = await this.getUser()
  if (!user) return null
  const secret = user.totpcode
  if (!secret) return null
  const totp = new OTPAuth.TOTP({
    digits,
    period,
    secret
  })
  const code = totp.generate()
  const currentSeconds = Math.round(new Date().getTime() / 1000.0)
  const updateTime = period - (currentSeconds % period)
  const progress = (updateTime / period) * 100
  return {
    code,
    updateTime,
    progress
  }
}

SSOClient.prototype.checkTotp = async function (code) {
  const token = await this.getToken()
  if (!token) {
    this.logout()
    return false
  }
  try {
    const response = await this.x.post('/auth/totp', { token, code })
    return response.data.data
  } catch (e) {
    console.trace(e)
    if (e.response.status === 401) {
      const renovation = await this.refreshToken()
      if (renovation) {
        const refreshedToken = await this.getToken()
        if (!refreshedToken) {
          this.logout()
          return false
        }
        const response = await this.x.post('/auth/totp', { token: refreshedToken, code })
        return response.data.data
      }
      this.logout()
    }
    return false
  }
}

SSOClient.prototype.onAuthStateChanged = function (watcher) {
  this.authStateSubscribers = [...this.authStateSubscribers, watcher]
}

export default SSOClient
